import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Save, AtSign,
  CheckCircle, XCircle, Loader,
} from 'lucide-react';

export default function EditProfile() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name:           user?.name           || '',
    username:       user?.username       || '',
    callNumber:     user?.callNumber     || '',
    whatsappNumber: user?.whatsappNumber || '',
    location:       user?.location       || '',
    gender:         user?.gender         || 'prefer_not_to_say',
  });
  const [errors,         setErrors]         = useState({});
  const [saving,         setSaving]         = useState(false);
  const [usernameStatus, setUsernameStatus] = useState('available'); // own username starts available
  const checkRef = useRef(null);

  // Live username check — skip if same as current username
  useEffect(() => {
    const username = form.username.trim();
    if (!username) { setUsernameStatus(null); return; }

    // If unchanged, it's available (it's theirs)
    if (username === user?.username) {
      setUsernameStatus('available');
      return;
    }

    if (!/^[a-z0-9_]{3,20}$/.test(username)) {
      setUsernameStatus('invalid');
      return;
    }

    setUsernameStatus('checking');
    clearTimeout(checkRef.current);
    checkRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/users/check-username/' + username);
        setUsernameStatus(data.available ? 'available' : 'taken');
      } catch { setUsernameStatus(null); }
    }, 500);
  }, [form.username, user?.username]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const val = name === 'username'
      ? value.toLowerCase().replace(/[^a-z0-9_]/g, '')
      : value;
    setForm(f => ({ ...f, [name]: val }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const validate = () => {
    const e   = {};
    const ph  = /^[6-9]\d{9}$/;
    if (!form.name.trim())             e.name           = 'Name is required.';
    if (usernameStatus === 'taken')    e.username        = 'Username is already taken.';
    if (usernameStatus === 'invalid')  e.username        = 'Invalid format.';
    if (form.callNumber && !ph.test(form.callNumber))
      e.callNumber = 'Enter a valid 10-digit number.';
    if (form.whatsappNumber && !ph.test(form.whatsappNumber))
      e.whatsappNumber = 'Enter a valid 10-digit number.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (usernameStatus === 'checking') {
      toast.error('Please wait for username check to complete.');
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.put('/users/profile', form);
      updateUser(data.user);
      toast.success('Profile updated!');
      navigate('/app/profile');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update.');
    } finally { setSaving(false); }
  };

  const StatusIcon = () => {
    if (usernameStatus === 'checking')  return <Loader size={15} className="spin" style={{ color: 'var(--text-light)' }} />;
    if (usernameStatus === 'available') return <CheckCircle size={15} style={{ color: 'var(--success)' }} />;
    if (usernameStatus === 'taken')     return <XCircle size={15} style={{ color: 'var(--danger)' }} />;
    if (usernameStatus === 'invalid')   return <XCircle size={15} style={{ color: 'var(--danger)' }} />;
    return null;
  };

  return (
    <div className="sell-page fade-in">
      <div className="sell-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2>Edit Profile</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Update your details
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: 480 }} noValidate>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

          {/* Name */}
          <div className="form-group">
            <label className="label">Full Name</label>
            <input
              className={'input ' + (errors.name ? 'input-error' : '')}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your full name"
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          {/* Username */}
          <div className="form-group">
            <label className="label">
              <AtSign size={14} style={{ display: 'inline', marginRight: 5 }} />
              Username
            </label>

            <div className="username-input-wrap">
              <span className="username-prefix">@</span>
              <input
                className={'input username-input ' + (errors.username ? 'input-error' : '')}
                name="username"
                value={form.username}
                onChange={handleChange}
                maxLength={20}
                autoComplete="off"
              />
              <span className="username-status-icon"><StatusIcon /></span>
            </div>

            {/* Status message */}
            {usernameStatus === 'available' && form.username !== user?.username && (
              <span style={{ fontSize: '0.78rem', color: 'var(--success)' }}>
                ✓ @{form.username} is available!
              </span>
            )}
            {usernameStatus === 'taken' && (
              <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                ✗ @{form.username} is already taken.
              </span>
            )}
            {usernameStatus === 'invalid' && (
              <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                Only lowercase letters, numbers and underscores (3–20 chars).
              </span>
            )}
            {errors.username && <span className="error-text">{errors.username}</span>}
            <span className="helper-text">
              Current: @{user?.username} · You can change your username once.
            </span>
          </div>

          {/* Call Number */}
          <div className="form-group">
            <label className="label">Calling Number</label>
            <input
              className={'input ' + (errors.callNumber ? 'input-error' : '')}
              name="callNumber"
              value={form.callNumber}
              onChange={handleChange}
              type="tel"
              maxLength={10}
              placeholder="10-digit number"
            />
            {errors.callNumber && <span className="error-text">{errors.callNumber}</span>}
          </div>

          {/* WhatsApp */}
          <div className="form-group">
            <label className="label">WhatsApp Number</label>
            <input
              className={'input ' + (errors.whatsappNumber ? 'input-error' : '')}
              name="whatsappNumber"
              value={form.whatsappNumber}
              onChange={handleChange}
              type="tel"
              maxLength={10}
              placeholder="10-digit number"
            />
            {errors.whatsappNumber && <span className="error-text">{errors.whatsappNumber}</span>}
          </div>

          {/* Location */}
          <div className="form-group">
            <label className="label">Location on Campus</label>
            <input
              className="input"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="e.g. Boys Hostel Block B, Room 210"
            />
          </div>

          {/* Gender */}
          <div className="form-group">
            <label className="label">Gender</label>
            <select
              className="input"
              name="gender"
              value={form.gender}
              onChange={handleChange}
            >
              <option value="prefer_not_to_say">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Email — read only */}
          <div className="form-group">
            <label className="label">Email</label>
            <input
              className="input"
              value={user?.email || ''}
              disabled
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            />
            <span className="helper-text">Email cannot be changed.</span>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || usernameStatus === 'checking' || usernameStatus === 'taken' || usernameStatus === 'invalid'}
          >
            {saving
              ? <><div className="spinner" /> Saving...</>
              : <><Save size={16} /> Save Changes</>
            }
          </button>
        </div>
      </form>
    </div>
  );
}